/**
 * Utility functions for managing modal hierarchy and interactions
 */

import { useEffect } from "react";

/**
 * Blocks interaction with all modal dialogs except the most recent one
 * by applying visual dimming and preventing pointer events
 */
export const blockBackgroundModals = (): void => {
  const allModals = document.querySelectorAll(".Polaris-Modal-Dialog");

  if (allModals.length > 1) {
    // Gray out all previous modals (all except the last one which is the current)
    allModals.forEach((modal, index) => {
      if (index < allModals.length - 1) {
        const modalElement = modal as HTMLElement;
        modalElement.style.opacity = "0";
        modalElement.style.pointerEvents = "none";

        // Also target all interactive elements inside the modal
        const interactiveElements = modalElement.querySelectorAll(
          'button, input, select, textarea, [role="button"], [tabindex], a'
        );
        interactiveElements.forEach((element) => {
          (element as HTMLElement).style.pointerEvents = "none";
        });

        // Add a click-blocking overlay
        const existingOverlay = modalElement.querySelector(
          ".modal-block-overlay"
        );
        if (!existingOverlay) {
          const overlay = document.createElement("div");
          overlay.className = "modal-block-overlay";
          overlay.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 9999;
            background: transparent;
            pointer-events: auto;
          `;
          modalElement.style.position = "relative";
          modalElement.appendChild(overlay);
        }
      }
    });
  }
};

/**
 * Restores all modal dialogs to their normal state
 * by removing visual dimming and restoring pointer events
 */
export const restoreAllModals = (): void => {
  const allModals = document.querySelectorAll(".Polaris-Modal-Dialog");

  allModals.forEach((modal) => {
    const modalElement = modal as HTMLElement;
    modalElement.style.opacity = "";
    modalElement.style.pointerEvents = "";

    // Restore all interactive elements
    const interactiveElements = modalElement.querySelectorAll(
      'button, input, select, textarea, [role="button"], [tabindex], a'
    );
    interactiveElements.forEach((element) => {
      (element as HTMLElement).style.pointerEvents = "";
    });

    // Remove the blocking overlay
    const overlay = modalElement.querySelector(".modal-block-overlay");
    if (overlay) {
      overlay.remove();
    }
  });
};

/**
 * Custom hook for managing modal hierarchy
 * Automatically blocks background modals when the current modal is open
 * and restores them when closed
 *
 * @param isOpen - Whether the current modal is open
 */
export const useModalHierarchy = (isOpen: boolean): void => {
  useEffect(() => {
    if (isOpen) {
      blockBackgroundModals();
    }

    return () => {
      restoreAllModals();
    };
  }, [isOpen]);
};
